import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  RiBarChartBoxLine,
  RiTimeLine,
  RiDatabase2Line,
  RiArrowUpLine,
  RiArrowDownLine,
  RiFileCodeLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
} from '@remixicon/react';

export function AnalyticsPage() {
  const { data: session } = useSession();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQueries: 0,
    avgExecutionTime: 0,
    successRate: 0,
    totalDatabases: 0,
    queriesThisWeek: 0,
    slowestQuery: 0,
  });

  useEffect(() => {
    if (session?.user?.id) {
      loadAnalytics();
    }
  }, [session?.user?.id]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const data = await api.getAnalyticsStats(session!.user!.id);
      setStats(data);
    } catch (error) {
      console.error('Failed to load analytics', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Queries',
      value: stats.totalQueries.toLocaleString(),
      description: '+12% from last month',
      icon: RiFileCodeLine,
      trend: 'up',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Avg Execution Time',
      value: `${stats.avgExecutionTime}ms`,
      description: '-8% from last month',
      icon: RiTimeLine,
      trend: 'down',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Success Rate',
      value: `${stats.successRate}%`,
      description: '+2% from last month',
      icon: RiCheckboxCircleLine,
      trend: 'up',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      title: 'Active Databases',
      value: stats.totalDatabases,
      description: 'Connected databases',
      icon: RiDatabase2Line,
      trend: null,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ];

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b">
          <div className="flex items-center gap-2 px-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Analytics</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3">
            <ModeToggle />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <RiBarChartBoxLine className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Analytics</h1>
                  <p className="text-muted-foreground">
                    Monitor your database performance and query metrics
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {statCards.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {stat.title}
                      </CardTitle>
                      <div className={`flex items-center justify-center w-8 h-8 rounded-md ${stat.bgColor}`}>
                        <stat.icon className={`w-4 h-4 ${stat.color}`} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        {stat.trend === 'up' && (
                          <RiArrowUpLine className="w-3 h-3 text-green-500" />
                        )}
                        {stat.trend === 'down' && (
                          <RiArrowDownLine className="w-3 h-3 text-green-500" />
                        )}
                        <span>{stat.description}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Charts Section */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Query Performance</CardTitle>
                  <CardDescription>
                    Average execution time over the last 30 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <RiBarChartBoxLine className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Chart visualization coming soon</p>
                      <p className="text-xs mt-1">Will show performance trends</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Query Volume</CardTitle>
                  <CardDescription>
                    Number of queries executed per day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <RiBarChartBoxLine className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Chart visualization coming soon</p>
                      <p className="text-xs mt-1">Will show query volume trends</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Query Activity</CardTitle>
                <CardDescription>
                  Latest queries executed across all databases
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center py-8 text-muted-foreground">
                    <RiFileCodeLine className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No recent activity</p>
                    <p className="text-xs mt-1">Execute some queries to see them here</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
